import React, { useState } from 'react';
import { Shield, Check, HelpCircle, ChevronDown, ChevronUp, Target, Clock, Award, FileCheck, Users, Zap, FileText, Lock, BookOpen } from 'lucide-react';
import UniversalPopupForm from '../../components/UniversalPopupForm';
import SEO from '../../components/SEO';

const CopyrightRegistration = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const benefits = [
    {
      title: 'Legal Proof of Ownership',
      description: 'Establish official documentation of your copyright ownership for creative works across Canada.'
    },
    {
      title: 'Legal Protection',
      description: 'Enable legal action against infringement in Canadian courts with registered copyright.'
    },
    {
      title: 'Lifetime Coverage',
      description: 'Protection lasts for the creator\'s lifetime plus 50 years under Canadian law.'
    },
    {
      title: 'Public Record',
      description: 'Creates a public record of your copyright ownership through the Canadian Copyright Office.'
    },
    {
      title: 'Commercial Value',
      description: 'Strengthens your position in licensing and commercial agreements for your creative works.'
    },
    {
      title: 'Creative Freedom',
      description: 'Work with confidence knowing your creative expressions are officially protected.'
    }
  ];

  const faqs = [
    {
      question: 'How long does copyright registration take in Canada?',
      answer: 'Copyright registration with the Canadian Copyright Office typically takes 3-6 months. However, your copyright exists automatically upon creation; registration provides legal proof and additional benefits.'
    },
    {
      question: 'What types of works can be copyrighted?',
      answer: 'In Canada, you can copyright literary works, musical works, artistic works (including photographs), dramatic works, software/code, and other original creative expressions fixed in a tangible form.'
    },
    {
      question: 'Do I need to register to have copyright protection?',
      answer: 'Copyright exists automatically in Canada, but registration provides important legal benefits including proof of ownership, public record, and stronger legal standing in infringement cases.'
    },
    {
      question: 'How long does copyright protection last?',
      answer: 'Copyright protection in Canada lasts for the life of the creator plus 50 years. For corporate works, protection extends 50 years from the date of first publication.'
    },
    {
      question: 'Can I copyright a business name or logo?',
      answer: 'Business names are protected by trademark law, not copyright. However, logos with artistic elements can be copyrighted. We recommend trademark registration for business names and logos.'
    },
    {
      question: 'What happens if someone infringes my copyright?',
      answer: 'With registered copyright, you can take legal action in Canadian courts. Our experts can guide you through enforcement options and connect you with specialized legal resources.'
    }
  ];

  const copyrightPricing = [
    {
      name: 'Basic',
      features: [
        'Copyright Analysis',
        'Work Classification',
        'Application Preparation',
        'Copyright Office Filing',
        'Status Tracking'
      ],
      recommended: false,
      badge: null
    },
    {
      name: 'Standard',
      features: [
        'Everything in Basic',
        'Priority Processing',
        'Expert Review',
        'Certificate Delivery',
        'Email Support',
        'Registration Confirmation'
      ],
      recommended: true,
      badge: 'Recommended'
    },
    {
      name: 'Deluxe',
      features: [
        'Everything in Standard',
        'Multiple Works Registration',
        'Legal Consultation',
        'Infringement Monitoring (6 Months)',
        'Licensing Agreement Template',
        'Priority Phone Support',
        'Lifetime Registration Support'
      ],
      recommended: false,
      badge: 'Best Seller'
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Copyright Registration Services"
        description="Secure your creative works with Canadian copyright registration. Protect your original content, art, music, and literary works with official documentation."
        keywords="copyright registration Canada, protect creative works, Canadian copyright office, intellectual property protection, copyright filing"
      />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden bg-gradient-to-br from-slate-900 via-teal-900 to-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1456324463128-7ff6903988d8"
            alt="Creative writing and art"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-teal-900/90 to-slate-900/95"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block bg-teal-600/30 backdrop-blur-sm border border-teal-400/30 rounded-full px-4 py-2 mb-6">
              <span className="text-white font-semibold">Canadian Copyright Registration</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6">
              Copyright Registration Services
            </h1>
            <p className="text-xl text-slate-200 leading-relaxed mb-8">
              Protect your creative works with official copyright registration. Professional, reliable, and affordable services for Canadian creators.
            </p>
            <button
              onClick={() => setIsPopupOpen(true)}
              className="bg-yellow-400 text-slate-900 font-bold px-8 py-4 rounded-lg hover:bg-yellow-500 hover:scale-110 transform hover:scale-105 transition-all duration-200 shadow-xl"
            >
              Start Your Registration
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Professional Help - Image Left, Content Right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-teal-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1515378791036-0648a3ef77b2"
                alt="Creative work and copyright protection"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="bg-green-100 rounded-full p-2">
                    <Award className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="font-bold text-gray-900">Expert Registration</span>
                </div>
                <p className="text-sm text-gray-600">Professional Copyright Filing</p>
              </div>
            </div>

            <div>
              <div className="inline-block bg-teal-100 text-teal-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Professional Advantage
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Professional Help Matters
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                While copyright exists automatically, proper registration provides crucial legal benefits. Our experts ensure your copyright registration is complete, accurate, and provides maximum protection.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-100 rounded-lg p-3 flex-shrink-0">
                    <Target className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Proper Classification</h3>
                    <p className="text-gray-600">Correct categorization of your creative works ensures comprehensive protection.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-100 rounded-lg p-3 flex-shrink-0">
                    <FileCheck className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Complete Documentation</h3>
                    <p className="text-gray-600">All Canadian Copyright Office requirements met with precision.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-100 rounded-lg p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Save Time & Effort</h3>
                    <p className="text-gray-600">Avoid errors and streamline the registration process with expert guidance.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-b from-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Benefits of Copyright Registration
            </h2>
            <p className="text-lg text-gray-600">
              Why Canadian creators should register their copyrights
            </p>
          </div>

          <div className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 pt-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-blue-100 min-w-[85%] md:min-w-0 snap-center"
              >
                <div className="bg-teal-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Copyright Office Process - Content Left, Image Right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-green-100 text-green-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Copyright Office Expertise
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Navigate Canadian Copyright Requirements With Confidence
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                The Canadian Copyright Office has specific filing requirements and procedures. Our team has successfully registered hundreds of copyrights and knows exactly what's needed for approval.
              </p>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 mb-6">
                <h3 className="font-bold text-gray-900 mb-4 text-xl">What We Handle:</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-teal-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Work analysis and proper classification</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-teal-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Complete copyright application preparation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-teal-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Filing with Canadian Copyright Office</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-teal-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Response to office inquiries</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-teal-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Ongoing status monitoring</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsPopupOpen(true)}
                className="bg-teal-600 text-white font-semibold px-8 py-4 rounded-lg hover:from-yellow-400 hover:to-yellow-500 transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                Start Your Application
              </button>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-green-400 to-blue-500 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85"
                alt="Copyright documentation process"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-500 rounded-full p-2">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Copyright Office</div>
                    <div className="text-sm text-gray-600">Experts</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fast & Reliable Service - Image Left, Content Right */}
      <section className="py-20 bg-gradient-to-b from-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e"
                alt="Fast copyright processing"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-teal-600 text-white rounded-xl shadow-xl p-6 max-w-xs">
                <Zap className="w-8 h-8 mb-2" />
                <div className="font-bold text-lg">24-48 Hour Response</div>
                <div className="text-sm text-slate-200">Lightning-fast service</div>
              </div>
            </div>

            <div>
              <div className="inline-block bg-purple-100 text-purple-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Quick & Efficient
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Fast Processing, Quality Results
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We understand that protecting your creative works is urgent. Our streamlined process ensures your copyright application is prepared and filed quickly without compromising accuracy.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-600 rounded-full p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Quick Turnaround</h3>
                    <p className="text-gray-600">Initial review and copyright application prepared within 24-48 hours.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-600 rounded-full p-3 flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Dedicated Support</h3>
                    <p className="text-gray-600">Personal case manager keeps you updated throughout the entire process.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-600 rounded-full p-3 flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Quality Guaranteed</h3>
                    <p className="text-gray-600">Expert handling ensures your registration is complete and accurate.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our 3-Step Registration Process
            </h2>
            <p className="text-lg text-gray-600">
              Simple, transparent, and efficient copyright filing
            </p>
          </div>

          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 pt-8">
            <div className="relative bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg border border-blue-100 transform transition-all duration-300 hover:scale-105 hover:shadow-xl min-w-[85%] md:min-w-0 snap-center">
              <div className="absolute -top-6 left-8 bg-teal-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                1
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Consultation & Review
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  We review your creative work and gather necessary information to prepare your copyright registration application for the Canadian Copyright Office.
                </p>
              </div>
            </div>

            <div className="relative bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg border border-blue-100 transform transition-all duration-300 hover:scale-105 hover:shadow-xl min-w-[85%] md:min-w-0 snap-center">
              <div className="absolute -top-6 left-8 bg-teal-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                2
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Filing & Submission
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Our experts prepare and file your application with the Canadian Copyright Office, ensuring all documentation meets federal requirements for creative works.
                </p>
              </div>
            </div>

            <div className="relative bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg border border-blue-100 transform transition-all duration-300 hover:scale-105 hover:shadow-xl min-w-[85%] md:min-w-0 snap-center">
              <div className="absolute -top-6 left-8 bg-teal-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                3
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Certificate & Protection
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  We monitor your application status and secure your copyright registration certificate, providing official proof of ownership for your creative works in Canada.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gradient-to-b from-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Affordable Copyright Registration Packages
            </h2>
            <p className="text-lg text-gray-600">
              Choose the package that best fits your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {copyrightPricing.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105 ${
                  plan.recommended ? 'ring-2 ring-blue-600 scale-105' : ''
                }`}
              >
                {plan.badge && (
                  <div className="absolute top-0 right-0 bg-teal-600 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                    {plan.badge}
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                  <div className="mb-6">
                    <p className="text-gray-600 text-sm">Contact us for pricing</p>
                  </div>
                  <ul className="space-y-3 mb-8 max-h-64 overflow-y-auto">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setIsPopupOpen(true)}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      plan.recommended
                        ? 'bg-teal-600 text-white hover:bg-yellow-400 hover:text-slate-900'
                        : 'bg-slate-200 text-slate-900 hover:bg-yellow-400 font-semibold'
                    }`}
                  >
                    Get a Quote
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Common questions about Canadian copyright registration
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="relative lg:sticky lg:top-8">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1521737711867-e3b97375f902"
                alt="Copyright consultation"
                className="relative rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-6">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Still Have Questions?</h3>
                <p className="text-gray-600 text-sm mb-4">Our copyright experts are here to help answer all your registration questions.</p>
                <button
                  onClick={() => setIsPopupOpen(true)}
                  className="w-full bg-teal-600 text-white font-semibold py-2 px-4 rounded-lg hover:from-yellow-400 hover:to-yellow-500 transition-all text-sm"
                >
                  Contact Us
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-start space-x-4 flex-1">
                      <HelpCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900 text-lg pr-4">{faq.question}</span>
                    </div>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <div className="pl-10 text-gray-600 leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden bg-gradient-to-br from-slate-900 via-teal-900 to-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1517935706615-2717063c2225"
            alt="Canadian art and creativity"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-teal-900/90 to-slate-900/95"></div>
          
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block animate-bounce mb-8">
            <div className="bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border-2 border-white/30">
              <span className="text-white font-bold text-sm">🍁 Trusted by Canadian Creators</span>
            </div>
          </div>

          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Protect Your Creative Work<br />
            <span className="bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">
              For a Lifetime and Beyond
            </span>
          </h2>
          
          <p className="text-2xl text-slate-200 mb-12 max-w-3xl mx-auto leading-relaxed">
            Protect your creative works with official registration. Start your copyright journey today with Canada's trusted experts.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <button
              onClick={() => setIsPopupOpen(true)}
              className="group relative bg-white text-teal-600 font-bold px-10 py-5 rounded-xl hover:bg-yellow-400 hover:text-slate-900 transform hover:scale-110 transition-all duration-300 shadow-2xl"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <Shield className="w-6 h-6" />
                <span className="text-lg">Register Your Copyright</span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-xl opacity-0 group-hover:opacity-20 blur transition-opacity duration-300"></div>
            </button>
            <button
              className="js-chat group relative bg-teal-600 text-white font-bold px-10 py-5 rounded-xl hover:bg-yellow-400 hover:text-slate-900 transform hover:scale-110 transition-all duration-300 shadow-2xl"
            >
              <span className="relative z-10 text-lg">Chat Now</span>
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">Life+50</div>
              <div className="text-slate-200 text-sm">Years Protection</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">24h</div>
              <div className="text-slate-200 text-sm">Fast Response</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">500+</div>
              <div className="text-slate-200 text-sm">Works Protected</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">Expert</div>
              <div className="text-slate-200 text-sm">Guidance</div>
            </div>
          </div>

          <p className="text-blue-200 mt-12 text-sm">
            Protecting Canadian creativity • Professional Copyright Services
          </p>
        </div>

        <div className="absolute top-10 left-10 w-20 h-20 bg-blue-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
      </section>

      <UniversalPopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default CopyrightRegistration;
